import React from 'react';
import useSharePdf from '../hooks/useSharePdf';
import { useDepartment } from '../contexts/DepartmentContext';
import { buildOrderDoc } from '../utils/orderPdf';
import { orderPdfFilename, orderPdfTitle } from '../utils/orderPdfMeta';
import { MENU_ITEM_BASE } from './OrderActionsMenu';

/**
 * The Download PDF / Print… / Share… rows of the order actions menu.
 *
 * 🔑 **THIS IS `OrderPdfButton`'S DROPDOWN, FLATTENED INTO THE ACTIONS PANEL.**
 * Product owner, 2026-08-10: *"We will also include an export excell and print
 * options in the action menu"*. Same document, same filename, same title, same
 * three actions, same order — only the chrome is gone, because a dropdown
 * inside a dropdown is broken here in two independent ways (the
 * `overflow-y-auto` clip and the two document-level `mousedown` close
 * listeners; both are documented at `hooks/useSharePdf.js`).
 *
 * 🚨 **IT IS A COMPONENT, NOT A `useOrderPdfActions()` HOOK, AND THAT IS
 * FORCED — NOT A PREFERENCE.** Both pages build their action items INSIDE
 * `orders.map(order => …)`, and a hook cannot be called from a loop body
 * without breaking the rules of hooks. The PDF actions need their own
 * `pendingAction` state per row, so they have to be a mounted component. That
 * is exactly what `OrderActionsMenu`'s `{ key, render }` item shape exists for.
 *
 * 🚨 **NO PERMISSION GATE, DELIBERATELY, AND IT MATCHES WHAT IT REPLACES.**
 * `<OrderPdfButton>` was rendered unconditionally in the quick-actions row
 * (`DepartmentOrders.js`, pre-2026-08-10), and the PDF is built client-side
 * from the detail payload the caller has already been served — it discloses
 * nothing they are not already looking at. The one role-dependent thing in the
 * document (whether the title carries the PO or the ORD reference) is decided
 * by `orderPdfMeta`'s `isAdmin`/`isBuyer` terms exactly as before.
 *
 * ⚠️ **NEW ON `Approvals.js`, and that is an ADDITION rather than a
 * relocation** — that page never had an `OrderPdfButton`. Taken deliberately
 * on 2026-08-10 because the owner asked for "print options in the action menu"
 * and To Approve is an order surface with the same detail payload; recorded
 * here and in CLAUDE.md so it is not later read as drift.
 *
 * Props:
 *  - order: the DETAIL payload (never the list row — the PDF renders line items)
 *  - supplierContact: optional, passed straight through to `buildOrderDoc`
 *  - onDone: called after a completed action, so the panel can close
 *  - testIdPrefix: e.g. `dept-2` / `approval-2`
 */
// `hidePrintWhenShareable` (Dept Orders' phone menu, owner 2026-10-04): a print
// dialog is rarely useful on a phone; Share (the native share sheet) is. Only
// when Share is actually offered, so a browser with no file sharing keeps Print.
export default function OrderPdfMenuItems({ order, supplierContact = null, onDone, testIdPrefix, hidePrintWhenShareable = false }) {
  // Role flags drive the document title (PO: for buyer/admin, ORD-XXX
  // otherwise) — the same two flags `OrderPdfButton` reads, from the same
  // context, for the same purpose.
  const { isAdmin, isBuyer } = useDepartment();

  const {
    pendingAction,
    canShare,
    handleDownload,
    handlePrint,
    handleShare,
  } = useSharePdf({
    renderPdfMakeDoc: () => buildOrderDoc({ order, supplierContact, isAdmin, isBuyer }),
    filename: order ? orderPdfFilename(order) : 'order.pdf',
    title: order ? orderPdfTitle(order, { isAdmin, isBuyer }) : 'Order',
    orientation: 'portrait',
    onActionComplete: onDone,
  });

  if (!order) return null;

  const tone = 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700';

  return (
    <>
      <button
        type="button"
        role="menuitem"
        onClick={(e) => { e.stopPropagation(); handleDownload(); }}
        disabled={!!pendingAction}
        data-testid={`${testIdPrefix}-menu-download-pdf`}
        title="Download this order as a PDF"
        className={`${MENU_ITEM_BASE} ${tone}`}
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
        Download PDF
        {pendingAction === 'download' && (
          <span className="text-[10px] text-gray-400 ml-auto">Generating…</span>
        )}
      </button>
      {!(hidePrintWhenShareable && canShare) && <button
        type="button"
        role="menuitem"
        onClick={(e) => { e.stopPropagation(); handlePrint(); }}
        disabled={!!pendingAction}
        data-testid={`${testIdPrefix}-menu-print`}
        title="Print this order"
        className={`${MENU_ITEM_BASE} ${tone}`}
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
        </svg>
        Print…
        {pendingAction === 'print' && (
          <span className="text-[10px] text-gray-400 ml-auto">Generating…</span>
        )}
      </button>}
      {/* Same `canShare` capability probe `SharePdfButton` uses — the row is
          absent, not disabled, on a browser with no file-share support. */}
      {canShare && (
        <button
          type="button"
          role="menuitem"
          onClick={(e) => { e.stopPropagation(); handleShare(); }}
          disabled={!!pendingAction}
          data-testid={`${testIdPrefix}-menu-share`}
          title="Share this order as a PDF"
          className={`${MENU_ITEM_BASE} ${tone}`}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
          </svg>
          Share…
          {pendingAction === 'share' && (
            <span className="text-[10px] text-gray-400 ml-auto">Generating…</span>
          )}
        </button>
      )}
    </>
  );
}
